import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmationResultModal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { DonationMatchRequestModalResetToPendingProps } from './DonationMatchRequestModalResetToPending.types'

export function DonationMatchRequestModalResetToPending({
  donationMatchRequestId,
  isOpen,
  onClose,
  refresh
}: Readonly<DonationMatchRequestModalResetToPendingProps>): JSX.Element {
  const { t } = useTranslation()
  const { donationMatchingRequestService } = useServices()
  const [{ isPending }, { mutateAsync }] = useMutation(donationMatchingRequestService.postDonationMatchRequestRevert)
  const [modalState, setModalState] = useState<'success' | 'failure'>()

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({ donationMatchRequestId })
      setModalState('success')
    } catch {
      setModalState('failure')
    }
  }

  const handleClose = (): void => {
    const shouldRefresh = modalState === 'success'
    setModalState(undefined)
    onClose()

    if (shouldRefresh) {
      refresh()
    }
  }

  return (
    <ConfirmationResultModal
      success={{
        title: t('dialog.donationMatchRequestResetToPending.success.title'),
        description: t('dialog.donationMatchRequestResetToPending.success.description'),
        button: t('button.continue')
      }}
      failure={{
        title: t('dialog.donationMatchRequestResetToPending.error.title'),
        description: t('dialog.donationMatchRequestResetToPending.error.description'),
        button: t('button.close')
      }}
      title={t('dialog.donationMatchRequestResetToPending.title')}
      primaryButtonText={t('button.donationMatchRequest.resetToPending')}
      secondaryButtonText={t('button.cancel')}
      state={modalState}
      loading={isPending}
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onClose={handleClose}
      onFailureClose={handleClose}
      primaryBtnTestId="reset-match-request-to-pending"
      secondaryBtnTestId="cancel-reset-match-request-to-pending"
    >
      <Text size="small">{t('dialog.donationMatchRequestResetToPending.description')}</Text>
    </ConfirmationResultModal>
  )
}
